import React, { useState, useEffect } from 'react';
import buttonImage from '../buttonimage.webp';
import mirrorimage from '../mirrorimage.webp';
import backgroundImage from '../background.webp';
import cardBackgroundImage from '../cardbackground.webp';
import { supabase } from '../supabaseClient';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

export default function Communication({ goBack, goHome, handleSaveProgress }) {
    // Card picker state
    const [cards, setCards] = useState([]);
    const [shuffledCards, setShuffledCards] = useState([]);
    const [selectedCards, setSelectedCards] = useState([]);
    const [pickerVisible, setPickerVisible] = useState(false);
    const [clickedCardId, setClickedCardId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [savedReadings, setSavedReadings] = useState([]);
    const [cardsLoaded, setCardsLoaded] = useState(false);

    // Fetch cards for 'words' component
    useEffect(() => {
        const fetchCards = async () => {
            const { data, error } = await supabase
                .schema('public')
                .from('cards')
                .select('id, title, image_path, descriptions')
                .eq('component', 'words');
            if (error) {
                toast.error('Failed to load cards.');
                setCardsLoaded(false);
                return;
            }
            setCards(data || []);
            setShuffledCards([...(data || [])]);
            setCardsLoaded(true);
        };
        fetchCards();
        fetchSavedReadings();
        // eslint-disable-next-line
    }, []);

    // Fetch saved readings for this component
    const fetchSavedReadings = async () => {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;
            const { data, error } = await supabase
                .schema('public')
                .from('card_readings')
                .select('*')
                .eq('user_id', user.id)
                .eq('component', 'words')
                .order('created_at', { ascending: false });
            if (!error) setSavedReadings(data || []);
        } catch (err) {
            // ignore
        }
    };

    // Shuffle helper
    const shuffleArray = (array) => {
        const shuffled = [...array];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
    };

    // Card click handler
    const handleCardClick = (card) => {
        // Allow up to 6 cards
        if (selectedCards.length < 6 && !selectedCards.some(c => c.id === card.id)) {
            setSelectedCards([...selectedCards, card]);
            setClickedCardId(card.id);
            setTimeout(() => setClickedCardId(null), 200);
        }
    };

    // Start reading
    const handleStartReading = () => {
        setSelectedCards([]);
        setPickerVisible(!pickerVisible);
        if (cards.length > 0) setShuffledCards(shuffleArray([...cards]));
    };

    // Save reading
    const handleSaveReading = async () => {
        if (selectedCards.length !== 6) {
            toast.error('Please select exactly 6 cards to save your reading.');
            return;
        }
        setSaving(true);
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) {
                toast.error('User not logged in.');
                setSaving(false);
                return;
            }
            const readingData = {
                user_id: user.id,
                component: 'words',
                cards: selectedCards.map(card => card.id),
                descriptions: selectedCards.reduce((acc, card, idx) => {
                    acc[`position${idx + 1}`] = card.descriptions || 'No description';
                    return acc;
                }, {}),
            };
            const { error } = await supabase
                .schema('public')
                .from('card_readings')
                .insert(readingData);
            if (error) throw error;
            toast.success('Reading saved!');
            setSelectedCards([]);
            setPickerVisible(false);
            await fetchSavedReadings();
        } catch (err) {
            toast.error('Failed to save reading.');
        } finally {
            setSaving(false);
        }
    };

    // Load reading
    const handleLoadReading = (reading) => {
        if (!cardsLoaded || cards.length === 0) {
            toast.info('Loading card data. Please wait a moment and try again.');
            return;
        }

        const cardsById = cards.reduce((acc, card) => {
            acc[card.id] = card;
            return acc;
        }, {});

        const loadedCards = reading.cards.map((cardId) => {
            const card = cardsById[cardId];
            if (!card) {
                console.warn(`Card ID ${cardId} not found while loading saved reading.`);
                return null;
            }
            return card;
        }).filter(Boolean);

        if (loadedCards.length !== reading.cards.length) {
            toast.error('Some cards could not be loaded for this saved reading. Please refresh and try again.');
        }

        setSelectedCards(loadedCards);
        setPickerVisible(false);
    };

    // Delete reading
    const handleDeleteReading = async (readingId) => {
        await supabase
            .schema('public')
            .from('card_readings')
            .delete()
            .eq('id', readingId);
        await fetchSavedReadings();
    };

    // Responsive: detect mobile
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 600;

    // Card position titles for reading display
    const cardPositions = [
        'Card to represent you', 'An underlying issue', 'What to surrender?',
        'What needs to be seen', 'What helps', 'What hinders'
    ];

    // Card aspect ratio for business card (5:3), but make picker cards bigger/taller
    const cardAspectWidth = 180;
    const cardAspectHeight = 108;

    // Helper to filter out back images from card set
    const isNotBackImage = (card) => {
        if (!card.image_path) return true;
        return !card.image_path.toLowerCase().includes('wbackimage');
    };

    return (
        <>
            <ToastContainer />
            <style>
                {`
                @media (max-width: 600px) {
                    .communication-btn-row {
                        flex-direction: row !important;
                        gap: 6px !important;
                    }
                    .communication-btn {
                        width: 32vw !important;
                        min-width: 0 !important;
                        max-width: 100px !important;
                        padding: 10px 0 !important;
                        font-size: 16px !important;
                    }
                }
                `}
            </style>
            <div
                style={{
                    backgroundImage: `url(${backgroundImage})`,
                    backgroundSize: 'cover',
                    backgroundRepeat: 'no-repeat',
                    minHeight: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: '16px',
                }}
            >
                {/* Title */}
                <h3
                    style={{
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: '24px',
                        fontWeight: 'bold',
                        color: 'black',
                        marginBottom: '32px',
                    }}
                >
                    Communication
                </h3>


                {/* Communication Intro Card with Mirror Image filling top */}
                <div
                    style={{
                        width: '100%',
                        maxWidth: '600px',
                        borderRadius: '10px',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                        border: '2px solid black',
                        marginBottom: '24px',
                        marginTop: 0,
                        overflow: 'hidden',
                        background: 'transparent',
                    }}
                >
                    <img
                        src={mirrorimage}
                        alt="Mirror"
                        style={{
                            width: '100%',
                            borderRadius: '10px 10px 0 0',
                            objectFit: 'cover',
                            maxHeight: '220px',
                        }}
                    />
                    <div
                        style={{
                            backgroundImage: `url(${cardBackgroundImage})`,
                            backgroundSize: 'cover',
                            borderBottomLeftRadius: '10px',
                            borderBottomRightRadius: '10px',
                            padding: '24px',
                            textAlign: 'center',
                        }}
                    >
                        <div style={{
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '22px',
                            fontWeight: 'bold',
                            color: '#4B5563',
                            margin: '0 0 8px 0',
                            textAlign: 'center',
                        }}>
                            Communication
                        </div>
                        <div style={{
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '16px',
                            color: '#333',
                            textAlign: 'center',
                        }}>
                            communication begins with understanding ourselves
                        </div>
                    </div>
                </div>

                {/* Session Helper Cards Row */}
                <div
                    className="communication-session-helper-row"
                    style={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        gap: '16px',
                        width: '100%',
                        maxWidth: '600px',
                        marginBottom: '16px',
                    }}
                >
                    {/* Left Card */}
                    <div style={{
                        flex: 1,
                        border: '2px solid black',
                        borderRadius: '10px',
                        padding: '16px',
                        textAlign: 'left',
                        backgroundImage: `url(${cardBackgroundImage})`,
                        backgroundSize: 'cover',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                        marginBottom: isMobile ? '16px' : 0,
                    }}>
                        <div style={{ fontFamily: 'Times New Roman, Times, serif', fontWeight: 'bold', fontSize: '18px', marginBottom: '10px', textAlign: 'center' }}>*Session Helper*</div>
                        <div style={{ fontFamily: 'Times New Roman, Times, serif', fontWeight: 'bold', fontSize: '16px', margin: 0 }}>
                            <div style={{ fontWeight: 'bold', fontSize: '16px' }}>• Communication happens on many levels.</div>
                            <div style={{ fontWeight: 'bold', fontSize: '16px' }}>• Accusatory language can break down communication. Use "I" statements.</div>
                            <div style={{ fontWeight: 'bold', fontSize: '16px' }}>• Our minds can misinterpret information.</div>
                            <div style={{ fontWeight: 'bold', fontSize: '16px' }}>• Communication can clear up misunderstanding.</div>
                            <div style={{ fontWeight: 'bold', fontSize: '16px' }}>• Assumption is the easist place for misunderstanding to thrive.</div>
                            <div style={{ fontWeight: 'bold', fontSize: '16px' }}>• Communication can be used for gathering information, giving information, or even misleading with information</div>
                        </div>
                    </div>
                    {/* Right Card */}
                    <div style={{
                        flex: 1,
                        border: '2px solid black',
                        borderRadius: '10px',
                        padding: '16px',
                        textAlign: 'left',
                        backgroundImage: `url(${cardBackgroundImage})`,
                        backgroundSize: 'cover',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                    }}>
                        <div style={{ fontFamily: 'Times New Roman, Times, serif', fontWeight: 'bold', fontSize: '18px', marginBottom: '10px', textAlign: 'center' }}>*Session Helper*</div>
                        <div style={{ fontFamily: 'Times New Roman, Times, serif', fontWeight: 'bold', fontSize: '16px', margin: 0 }}>
                            <div style={{ fontWeight: 'bold', fontSize: '16px' }}>1. Are you able to communicate freely?</div>
                            <div style={{ fontWeight: 'bold', fontSize: '16px' }}>2. What needs to be said?</div>
                            <div style={{ fontWeight: 'bold', fontSize: '16px' }}>3. Is the purpose of the communication to understand?</div>
                            <div style={{ fontWeight: 'bold', fontSize: '16px' }}>4. What is not being said?</div>
                            <div style={{ fontWeight: 'bold', fontSize: '16px' }}>5. Have you tried writing a letter you do not send?</div>
                            <div style={{ fontWeight: 'bold', fontSize: '16px' }}>6. Are you clearly understanding what was communicated to you?</div>
                        </div>
                    </div>
                </div>

                {/* Cards */}
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '16px',
                        width: '100%',
                        maxWidth: '600px',
                    }}
                >
                    {/* Card 1 */}
                    <div
                        style={{
                            width: '100%',
                            border: '2px solid black',
                            borderRadius: '10px',
                            padding: '16px',
                            textAlign: 'center',
                            backgroundImage: `url(${cardBackgroundImage})`,
                            backgroundSize: 'cover',
                            boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                        }}
                    >
                        <p
                            style={{
                                fontFamily: 'Times New Roman, Times, serif',
                                fontSize: '16px',
                                fontWeight: 'normal',
                                color: 'black',
                                textAlign: 'center',
                                lineHeight: '1.5',
                                marginBottom: '16px'
                            }}
                        >
                            How we communicate and who we communicate with matters. It happens on many levels. Our body, our eyes, our words, our hearts,  our energy, and our minds can all convey and interpret information.  

We can communicate with ourselves, our pets, our intuition, and other people.   Some people can sense and interpret a wider range of energy than others and some are really good at interpreting the nuances of a specific topic. 

We can all build our skills in this area of life.  It can be an art to correctly perceive information and directly convey information.  

Sometimes communication is gleaned by what is not said instead.  
                        </p>
                    </div>

                    {/* Card 2 */}
                    <div
                        style={{
                            width: '100%',
                            border: '2px solid black',
                            borderRadius: '10px',
                            padding: '16px',
                            textAlign: 'center',
                            backgroundImage: `url(${cardBackgroundImage})`,
                            backgroundSize: 'cover',
                            boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                        }}
                    >
                        <p
                            style={{
                                fontFamily: 'Times New Roman, Times, serif',
                                fontSize: '16px',
                                color: 'black',
                            }}
                        >
                            There are some ways we can communicate that are less effective than others.  When we find ourselves in conflict with someone else it is helpful to check and see if the goal is to understand the other, or to be understood by the other.  Ask the other if they are willing to try and understand.  It helps a lot if we do not use accusatory language.  Start sentences with "I", instead of "You". Another important factor in communication can be a willingness to validate the other party before presenting conflicting information.  In order to validate someone else's perspective shared we must somewhat understand it.  When we take the time to let the other know that we have considered what they said and that it is valid (even if only to them), we create the environment that will be much more receptive to hearing our point of view as well, even if we disagree.  Our perspective is valid for us and theirs is for them.  Still, it can be very beneficial to learn of other perspectives.  Our awareness expands the more perspectives we are able to consider. Despite building good communication skills, sometimes we are not able to have productive communication with another person, and sometimes it can be unproductive to share our communication with the other when we know it is going to cause war.  If we have one of these situations and we still carry an unfinished conversation with us, it can help to write a letter we do not send.  We spend the time to express ourselves and see what we have harbored inside wanting to express and then we simply delete or destroy the letter instead of sending it.  It is helpful to process our own inner world by seeing it out in the letter.
                        </p>
                    </div>

                    {/* Card 3 */}
                    <div
                        style={{
                            width: '100%',
                            border: '2px solid black',
                            borderRadius: '10px',
                            padding: '16px',
                            textAlign: 'center',
                            backgroundImage: `url(${cardBackgroundImage})`,
                            backgroundSize: 'cover',
                            boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                        }}
                    >
                        <p
                            style={{
                                fontFamily: 'Times New Roman, Times, serif',
                                fontSize: '16px',
                                color: 'black',
                            }}
                        >
                            What are some circumstances that you have experienced difficult communication in? Would these simple changes have been effective in those circumstances? Who do you call when you need someone to talk to? The Oracle Card Reading below can offer words to reflect on.  What comes to mind when you read that word? Do you feel comfortable with that topic? Are there things you would not say out loud? All of your thoughts, feelings, and behaviors in response to these words can give you insight into yourself and your communication with others. 
                        </p>
                    </div>

                    {/* Move the communication meme image card here */}
                    <div
                        style={{
                            width: '100%',
                            border: '2px solid black',
                            borderRadius: '10px',
                            padding: '16px',
                            textAlign: 'center',
                            backgroundImage: `url(${cardBackgroundImage})`,
                            backgroundSize: 'cover',
                            boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <img
                            src="/images/communicationmeme.webp"
                            alt="You know how when you see two people arguing and you are not involved, you can see both sides and understand where the miscommunication is stemming from, maybe speculate why it is happening, and perhaps even clearly see a solution? The reality is.. whatever is in your head is yours.  Whatever is in their head is theirs.  Each of us have our own version of reality.  It is completely unfair to expect others to respond to our version of reality without having previously established that they wish to both understand our reality and participate in it. Most people are not using telepathy to communicate; therefore we owe it to ourselves, and our loved ones, to be open to understanding their perspective.  We cannot expect others to want to understand us, but we can ask them if they are willing to, and make sure we take the time to understand them"
                            style={{
                                width: '100%',
                                maxWidth: '360px',
                                height: 'auto',
                                marginBottom: '12px',
                                borderRadius: '8px',
                                border: '1px solid #4B5563',
                                objectFit: 'contain',
                                display: 'block',
                                marginLeft: 'auto',
                                marginRight: 'auto'
                            }}
                        />
                    </div>

                    {/* New Card with intimidation2 image */}
                    <div
                        style={{
                            width: '100%',
                            border: '2px solid black',
                            borderRadius: '10px',
                            padding: '16px',
                            textAlign: 'center',
                            backgroundImage: `url(${cardBackgroundImage})`,
                            backgroundSize: 'cover',
                            boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: '16px'
                        }}
                    >
                        <img
                            src="/images/intimidation2.webp"
                            alt="Title if you feel intimidated by someone inside parenthesis and they have not assualted you or physically harmed you end parenthesis. Here are some questions for reflection.  In what ways are you trying to control the way you are seen? In what ways do you feel inferior to them? In what way are you comparing their strengths to your weaknesses? Are you forgetting that we are a community meant to work together with our varied specialties? What power do you wish you had over them? Why are you competing with them? Where have you let your own boundary be violated with them? What would be so bad about being seen in your most authentic state? Does any part of you wish to be intimidating?"
                            style={{
                                width: '100%',
                                maxWidth: '360px',
                                height: 'auto',
                                marginBottom: '12px',
                                borderRadius: '8px',
                                border: '1px solid #4B5563',
                                objectFit: 'contain',
                                display: 'block',
                                marginLeft: 'auto',
                                marginRight: 'auto'
                            }}
                        />
                    </div>
                    {/* New Card with intimidation3 image */}
                    <div
                        style={{
                            width: '100%',
                            border: '2px solid black',
                            borderRadius: '10px',
                            padding: '16px',
                            textAlign: 'center',
                            backgroundImage: `url(${cardBackgroundImage})`,
                            backgroundSize: 'cover',
                            boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: '16px'
                        }}
                    >
                        <img
                            src="/images/intimidation3.webp"
                            alt="Title if you are trying to intimidate someone inside parenthesis without physical harm end parenthesis. Here are some questions for reflection.  Why do you feel more powerful than them? What weakness in yourself are you trying to avoid or project onto them? What would it mean about you if they had more value than you in a certain skill? What would be so bad about being seen in your most authentic state? How would it feel to express that you feel insecure in this experience? What can you do to fill your own need for security without involving others? Who used intimidation against you earlier on in your life?"
                            style={{
                                width: '100%',
                                maxWidth: '360px',
                                height: 'auto',
                                marginBottom: '12px',
                                borderRadius: '8px',
                                border: '1px solid #4B5563',
                                objectFit: 'contain',
                                display: 'block',
                                marginLeft: 'auto',
                                marginRight: 'auto'
                            }}
                        />
                    </div>
                </div>

                {/* Start Card Reading button */}
                <button
                    style={{
                        backgroundImage: `url(${buttonImage})`,
                        backgroundSize: 'cover',
                        border: '2px solid black',
                        borderRadius: '10px',
                        padding: '10px 20px',
                        width: 'auto',
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                        margin: '24px auto 0 auto',
                    }}
                    onClick={handleStartReading}
                >
                    <span style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }}>
                        This feature is a novelty and not required for your experience. You may skip this card picker.
                    </span>
                    {pickerVisible ? 'Hide Card Picker' : 'Start Card Reading'}
                </button>

                {/* Card Picker */}
                {pickerVisible && (
                    <div
                        style={{
                            width: '100vw',
                            maxWidth: '100vw',
                            padding: '8px 0',
                            margin: isMobile ? '0 auto' : '0 auto',
                            minHeight: isMobile ? '480px' : '360px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                        }}
                    >
                        {Array.from({ length: isMobile ? 4 : 3 }).map((_, rowIdx) => {
                            const filteredCards = shuffledCards.filter(isNotBackImage);
                            const cardsPerRow = Math.ceil(filteredCards.length / (isMobile ? 4 : 3));
                            // Use larger business card aspect ratio and increase overlap for mobile
                            const cardWidth = cardAspectWidth * (isMobile ? 0.95 : 1.0);
                            const cardHeight = cardAspectHeight * (isMobile ? 1.25 : 1.0); // taller for mobile
                            // Calculate overlap so that the row fits with margins on both sides
                            const containerWidth = typeof window !== 'undefined' ? window.innerWidth : 360;
                            const visibleCards = Math.min(cardsPerRow, Math.floor(containerWidth / (cardWidth * 0.45)));
                            const overlap = visibleCards > 1
                                ? Math.floor((cardWidth * visibleCards - (containerWidth * 0.92)) / (visibleCards - 1))
                                : 0;
                            const marginLeft = idx => (idx === 0 ? 'auto' : `-${Math.max(overlap, 0)}px`);
                            const marginRight = idx => (idx === cardsPerRow - 1 ? 'auto' : undefined);
                            return (
                                <div
                                    key={rowIdx}
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        justifyContent: 'center',
                                        marginBottom: '8px',
                                        width: '100%',
                                        overflow: 'hidden',
                                    }}
                                >
                                    {filteredCards
                                        .slice(rowIdx * cardsPerRow, (rowIdx + 1) * cardsPerRow)
                                        .map((card, idx, arr) => (
                                            <div
                                                key={card.id}
                                                style={{
                                                    width: `${cardWidth}px`,
                                                    height: `${cardHeight}px`,
                                                    cursor: 'pointer',
                                                    marginLeft: marginLeft(idx),
                                                    marginRight: marginRight(idx),
                                                    boxShadow: clickedCardId === card.id
                                                        ? '0 0 10px rgba(0, 255, 0, 0.5)'
                                                        : '0 2px 4px rgba(0, 0, 0, 0.2)',
                                                    transition: 'transform 0.2s, box-shadow 0.2s',
                                                    transform: clickedCardId === card.id ? 'scale(1.1)' : 'scale(1)',
                                                    zIndex: idx,
                                                    borderRadius: '8px',
                                                    background: 'none',
                                                    position: 'relative',
                                                    overflow: 'visible',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    backgroundImage: 'url(https://zxvjqkrfvproyblrjcst.supabase.co/storage/v1/object/public/card-images/words/wbackimage01.webp)', // ensure this is set
                                                    backgroundRepeat: 'no-repeat', // ensure no repeat
                                                    backgroundPosition: 'center', // center the image
                                                    backgroundSize: 'cover', // or 'contain' if you want the full image on each card
                                                }}
                                                onClick={() => handleCardClick(card)}
                                            >
                                                {/* ...existing code... */}
                                            </div>
                                        ))}
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Selected Cards (Reading Display) */}
                {selectedCards.filter(isNotBackImage).length > 0 && (
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
                            gridTemplateRows: isMobile ? 'repeat(3, 1fr)' : 'repeat(2, 1fr)',
                            gap: '12px',
                            marginTop: '16px',
                            background: 'white',
                            border: '2px solid black',
                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                            padding: '12px',
                            borderRadius: '8px',
                            maxWidth: '600px',
                            margin: '16px auto',
                            width: '100%',
                        }}
                    >
                        {selectedCards.filter(isNotBackImage).slice(0, isMobile ? 6 : 6).map((card, idx) => (
                            <div key={card.id} style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                boxShadow: '0 4px 6px rgba(0,0,0,0.3), 0 -4px 6px rgba(0,0,0,0.3)',
                                background: '#f8f8f8',
                                borderRadius: '8px',
                                padding: '8px',
                            }}>
                                <div style={{
                                    fontFamily: 'Times New Roman, Times, serif',
                                    fontSize: '14px',
                                    fontWeight: 'bold',
                                    color: '#4B5563',
                                    marginBottom: '4px',
                                    textAlign: 'center',
                                }}>
                                    {cardPositions[idx]}
                                </div>
                                <img
                                    src={`https://zxvjqkrfvproyblrjcst.supabase.co/storage/v1/object/public/${card.image_path}`}
                                    alt=""
                                    style={{
                                        width: isMobile ? `${cardAspectWidth * 0.9}px` : `${cardAspectWidth * 1.1}px`,
                                        height: isMobile ? `${cardAspectHeight * 0.9}px` : `${cardAspectHeight * 1.1}px`,
                                        objectFit: 'cover',
                                        borderRadius: '8px',
                                        marginBottom: '6px',
                                        background: '#fff',
                                    }}
                                />
                                <div style={{
                                    fontFamily: 'Times New Roman, Times, serif',
                                    fontSize: '12px',
                                    color: '#333',
                                    marginTop: '2px',
                                    textAlign: 'center',
                                    maxWidth: '120px',
                                    whiteSpace: 'pre-line',
                                }}>
                                    {card.descriptions || 'No description'}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Save Reading button */}
                {selectedCards.filter(isNotBackImage).length === 6 && (
                    <button
                        style={{
                            backgroundImage: `url(${buttonImage})`,
                            backgroundSize: 'cover',
                            border: '2px solid black',
                            borderRadius: '10px',
                            padding: '10px 20px',
                            width: 'auto',
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                            margin: '16px auto',
                        }}
                        onClick={handleSaveReading}
                        disabled={saving}
                    >
                        <span style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }}>
                            This feature is a novelty and not required for your experience. You may skip this card picker.
                        </span>
                        {saving ? 'Saving...' : 'Save Reading'}
                    </button>
                )}

                {/* Navigation Buttons */}
                <div
                    className="communication-btn-row"
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: '16px',
                        marginTop: '32px',
                    }}
                >
                    <button
                        className="communication-btn"
                        style={{
                            backgroundImage: `url(${buttonImage})`,
                            backgroundSize: 'cover',
                            border: '2px solid black',
                            borderRadius: '10px',
                            padding: '10px 20px',
                            width: '120px',
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                        }}
                        onClick={goBack}
                    >
                        Back
                    </button>
                    <button
                        className="communication-btn"
                        style={{
                            backgroundImage: `url(${buttonImage})`,
                            backgroundSize: 'cover',
                            border: '2px solid black',
                            borderRadius: '10px',
                            padding: '10px 20px',
                            width: '120px',
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                        }}
                        onClick={goHome}
                    >
                        Home
                    </button>
                    <button
                        className="communication-btn"
                        style={{
                            backgroundImage: `url(${buttonImage})`,
                            backgroundSize: 'cover',
                            border: '2px solid black',
                            borderRadius: '10px',
                            padding: '10px 20px',
                            width: '120px',
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                        }}
                        onClick={handleSaveProgress}
                    >
                        Save
                    </button>
                </div>

                {/* Saved Readings */}
                {savedReadings.length > 0 && (
                    <div
                        style={{
                            marginTop: '32px',
                            width: '100%',
                            maxWidth: '600px',
                            padding: '8px',
                        }}
                    >
                        <h4
                            style={{
                                fontFamily: 'Times New Roman, Times, serif',
                                fontSize: '20px',
                                fontWeight: 'bold',
                                color: '#4B5563',
                                textAlign: 'center',
                                marginBottom: '16px',
                            }}
                        >
                            Saved Readings
                        </h4>
                        {savedReadings.map((reading) => (
                            <div
                                key={reading.id}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '8px',
                                    border: '1px solid black',
                                    borderRadius: '8px',
                                    marginBottom: '8px',
                                    cursor: 'pointer',
                                    textAlign: 'left',
                                    fontFamily: 'Times New Roman, Times, serif',
                                    fontSize: '14px',
                                    color: '#4B5563',
                                    background: '#fff',
                                }}
                            >
                                <span onClick={() => handleLoadReading(reading)}>
                                    {new Date(reading.created_at).toLocaleString()}
                                </span>
                                <span
                                    style={{
                                        color: 'red',
                                        cursor: 'pointer',
                                        fontSize: '12px',
                                        marginLeft: '16px',
                                    }}
                                    onClick={async (e) => {
                                        e.stopPropagation();
                                        await handleDeleteReading(reading.id);
                                    }}
                                >
                                    -delete
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </>
    );
}
